import Phaser from 'phaser';
import { Item } from './Item';
import { TerritoryManager } from './TerritoryManager';
import { ItemData, ITEM, randomItemType } from '../config/items';

/**
 * 필드 아이템의 생명주기 관리.
 * - 스폰/만료는 호스트 권위 (tick은 호스트만 호출).
 * - 수신측은 applySpawn/applyDespawn/applySnapshot으로 로컬 목록을 맞춘다.
 * - "감싸 먹기" 판정은 checkPickup (영역 포함 아이템 반환).
 */
export class ItemManager {
  private scene: Phaser.Scene;
  private worldCenterX: number;
  private worldCenterY: number;
  private worldRadius: number;

  private sprites: Map<string, Item> = new Map();
  private meta: Map<string, { data: ItemData; spawnedAt: number }> = new Map();

  private spawnTimer: number = 0;

  private onSpawn?: (items: ItemData[]) => void;
  private onDespawn?: (ids: string[]) => void;

  constructor(
    scene: Phaser.Scene,
    worldCenterX: number,
    worldCenterY: number,
    worldRadius: number
  ) {
    this.scene = scene;
    this.worldCenterX = worldCenterX;
    this.worldCenterY = worldCenterY;
    this.worldRadius = worldRadius;
  }

  setOnSpawn(cb: (items: ItemData[]) => void): void {
    this.onSpawn = cb;
  }

  setOnDespawn(cb: (ids: string[]) => void): void {
    this.onDespawn = cb;
  }

  /** 호스트 전용: 주기 스폰 + 만료 처리 */
  tickHost(delta: number): void {
    this.spawnTimer += delta;
    if (this.spawnTimer >= ITEM.spawn.intervalMs) {
      this.spawnTimer = 0;
      if (this.meta.size < ITEM.spawn.maxOnField) {
        const spawned = this.spawnOne();
        if (spawned) this.onSpawn?.([spawned]);
      }
    }

    // 만료
    const now = Date.now();
    const expired: string[] = [];
    this.meta.forEach((m, id) => {
      if (now - m.spawnedAt > ITEM.spawn.expireMs) expired.push(id);
    });
    if (expired.length > 0) {
      this.removeItems(expired);
      this.onDespawn?.(expired);
    }
  }

  private spawnOne(): ItemData | null {
    const angle = Math.random() * Math.PI * 2;
    const dist = 120 + Math.random() * (this.worldRadius - 320);
    const x = Math.round(this.worldCenterX + Math.cos(angle) * dist);
    const y = Math.round(this.worldCenterY + Math.sin(angle) * dist);
    const id = `itm_${crypto.randomUUID().slice(0, 8)}`;
    const data: ItemData = { id, type: randomItemType(), x, y };
    this.createSprite(data);
    return data;
  }

  private createSprite(data: ItemData): void {
    if (this.sprites.has(data.id)) return;
    this.sprites.set(data.id, new Item(this.scene, data.id, data.type, data.x, data.y));
    this.meta.set(data.id, { data, spawnedAt: Date.now() });
  }

  /** 수신: 호스트가 스폰한 아이템 반영 */
  applySpawn(items: ItemData[]): void {
    for (const it of items) this.createSprite(it);
  }

  /** 수신: 만료/획득된 아이템 제거 */
  applyDespawn(ids: string[]): void {
    this.removeItems(ids);
  }

  /**
   * 수신: 스냅샷 머지 (늦게 입장한 클라이언트용).
   * 전체 교체가 아니라 "없는 것만 추가"한다 — 스냅샷 생성 이후 도착한 spawn
   * 이벤트를 늦은 스냅샷이 덮어써 지우는 것을 막는다. 제거는 collect/despawn 담당.
   */
  applySnapshot(items: ItemData[]): void {
    for (const it of items) this.createSprite(it);
  }

  /** 호스트가 신규 입장자에게 보낼 현재 목록 */
  getSnapshot(): ItemData[] {
    return [...this.meta.values()].map((m) => m.data);
  }

  /**
   * ownerId의 영역에 포함된(=감싼) 아이템 목록 반환.
   * 제거는 호출자가 collect 처리에서 removeItems로 수행한다.
   */
  checkPickup(
    territoryManager: TerritoryManager,
    ownerId: string
  ): ItemData[] {
    const picked: ItemData[] = [];
    this.meta.forEach((m) => {
      if (territoryManager.isInsideOwnTerritory(ownerId, m.data.x, m.data.y)) {
        picked.push(m.data);
      }
    });
    return picked;
  }

  removeItems(ids: string[]): void {
    for (const id of ids) {
      this.sprites.get(id)?.destroy();
      this.sprites.delete(id);
      this.meta.delete(id);
    }
  }

  destroy(): void {
    this.removeItems([...this.meta.keys()]);
  }
}
